import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
  Observable,
} from '@apollo/client';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { loadDevMessages, loadErrorMessages } from '@apollo/client/dev';
import { ErrorLink } from '@apollo/client/link/error';
import { CombinedGraphQLErrors, ServerError } from '@apollo/client/errors';
import * as Sentry from '@sentry/react';
import { map } from 'rxjs';
import {
  createCustomFetch,
  FETCH_TIMEOUT,
  getNetworkContext,
  isRetryableError,
  delay,
  MAX_RETRIES,
  RETRY_DELAY_MS,
} from '@lib/utils/customFetch';
import isAbortError from '@lib/utils/isAbortError';
import { createLoggingLink } from '@lib/services/logger';
import { hasAuthenticationError } from '@lib/utils/authErrorHandler';
import {
  hasTechnicalPauseError,
  handleTechnicalPauseError,
} from '@lib/utils/technicalPauseErrorHandler';
import { handleGraphQLErrors } from '@lib/utils/versionMismatchErrorHandler';
import { captureException } from '@lib/utils/captureException';
import { emitSessionExpired } from '@lib/utils/sessionExpiredEvent';
import { SW_REQUEST_TIMEOUT_MESSAGE } from '@lib/constants/fetch';
import { cacheResponseInIDB, getResponseFromIDB } from 'utils/indexedDBUtils';
import isWhoamiRequestBody from './utils/isWhoamiRequestBody';

const { VITE_API_URL: API_DOMAIN, VITE_AUTH_SCOPE, MODE } = import.meta.env;

if (MODE && ['development', 'test'].includes(MODE)) {
  loadDevMessages();
  loadErrorMessages();
}

const customHeaders = {
  'Content-Type': 'application/json',
  Accept: 'application/json',
  'X-Auth-Scope': VITE_AUTH_SCOPE,
  'Cache-Control': 'no-cache',
};

const cors = {
  xhrFields: { withCredentials: true },
  crossDomain: true,
  mode: 'cors',
  credentials: 'include',
} as const;

/**
 * Performs a single ServiceWorker fetch attempt
 */
function performServiceWorkerFetch(
  uri: RequestInfo | URL,
  options: RequestInit | undefined,
): Promise<Response> {
  return new Promise<Response>((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(
        new Error(`${SW_REQUEST_TIMEOUT_MESSAGE} after ${FETCH_TIMEOUT}ms`),
      );
    }, FETCH_TIMEOUT);

    const messageChannel = new MessageChannel();

    messageChannel.port1.onmessage = (event) => {
      clearTimeout(timeoutId);
      if (event.data.error) {
        const errorMessage = event.data.error?.message;
        // Return error response instead of rejecting to match Apollo's expectations
        resolve(
          new Response(
            JSON.stringify({
              errors: [{ message: errorMessage }],
            }),
            {
              status: event.data.status || 503,
              headers: { 'Content-Type': 'application/json' },
            },
          ),
        );
      } else {
        const fetchResponse = new Response(new Blob([event.data.body]), {
          status: event.data.status,
          statusText: event.data.statusText,
          headers: event.data.headers,
        });

        const requestId = fetchResponse.headers.get('x-request-id');
        if (requestId) {
          Sentry.setTag('request_id', requestId);
        }

        resolve(fetchResponse);
      }
    };

    if ('serviceWorker' in navigator) {
      const sanitizedOptions = { ...options };
      delete sanitizedOptions.signal;
      const serviceWorker = navigator.serviceWorker as ServiceWorkerContainer;
      serviceWorker.controller?.postMessage(
        { type: 'graphql-request', uri, options: sanitizedOptions },
        [messageChannel.port2],
      );
    }
  });
}

function hasServiceWorkerController() {
  return (
    'serviceWorker' in navigator &&
    !!(navigator.serviceWorker as ServiceWorkerContainer).controller
  );
}

/**
 * Direct fetch, used whenever the ServiceWorker isn't handling this request.
 *
 * It carries its own half of the offline `whoami` flow, mirroring what `sw.ts`
 * does inside the worker: cache a successful response, and serve the cached one
 * when the network is gone. The worker only sees a request once it *controls*
 * the page, and there are ordinary loads where it doesn't - the first load after
 * the cache is cleared (the freshly installed worker hasn't claimed the page
 * yet), a worker update cycle, a controller that disappears mid-retry. On those
 * loads the worker is not merely slower, it is absent from the request entirely,
 * so without this the session cache is neither written nor readable and an
 * offline launch has nothing to authenticate against.
 *
 * Both sides key the entry by the raw request body, so a row written here is
 * found by the worker and vice versa.
 */
async function fetchWithoutServiceWorker(
  uri: RequestInfo | URL,
  options?: RequestInit,
): Promise<Response> {
  const body = options?.body;
  const isWhoami = isWhoamiRequestBody(body);

  try {
    const response = await createCustomFetch(uri, options);

    if (isWhoami) {
      // Not awaited: this is a cache write riding along on the session request,
      // and it must not be able to delay - or, if IndexedDB stalls rather than
      // rejecting, indefinitely block - the response that gates authentication.
      // Dexie's lazy `open()` does exactly that while another tab holds an older
      // version of the database open. `cacheResponseInIDB` clones the response
      // synchronously before its first await, so running it detached is safe.
      cacheResponseInIDB(body, response).catch(() => undefined);
    }

    return response;
  } catch (error) {
    // Only `Whoami` is ever written to the cache, so looking anything else up
    // would just be a guaranteed miss on the way to the same rejection.
    if (isWhoami) {
      const cachedResponse = await getResponseFromIDB(body);
      if (cachedResponse) {
        return cachedResponse;
      }
    }
    throw error;
  }
}

/**
 * Custom fetch with ServiceWorker integration and retry logic
 * Uses ServiceWorker for GraphQL requests when available, falls back to
 * createCustomFetch with retry logic for direct fetch calls
 */
const customFetchWithServiceWorker = async (
  uri: RequestInfo | URL,
  options?: RequestInit,
): Promise<Response> => {
  // Use ServiceWorker for non-OPTIONS requests when available
  if (options?.method !== 'OPTIONS' && hasServiceWorkerController()) {
    let lastError: Error | null = null;

    // eslint-disable-next-line no-plusplus
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      // The controller can go away between attempts, or between the check
      // above and the `postMessage` below - a new worker activating, or the
      // page losing its controller. `performServiceWorkerFetch` posts through
      // an optional chain, so the message would silently go nowhere and the
      // request would hang for the full timeout before failing as an
      // unclassifiable error (a bogus "500" page). Take the direct path.
      if (!hasServiceWorkerController()) {
        return fetchWithoutServiceWorker(uri, options);
      }

      try {
        // eslint-disable-next-line no-await-in-loop
        const response = await performServiceWorkerFetch(uri, options);
        return response;
      } catch (error) {
        const errorInstance =
          error instanceof Error ? error : new Error(String(error));
        lastError = errorInstance;

        // Add context to Sentry
        Sentry.setContext('fetchError', {
          errorType: 'SERVICE_WORKER_ERROR',
          attempt: attempt + 1,
          maxRetries: MAX_RETRIES,
          timeout: FETCH_TIMEOUT,
          uri: typeof uri === 'string' ? uri : uri.toString(),
          errorMessage: errorInstance.message,
          ...getNetworkContext(),
        });

        // Retry for transient errors
        if (attempt < MAX_RETRIES && isRetryableError(error)) {
          const backoffDelay = RETRY_DELAY_MS * (attempt + 1);
          // eslint-disable-next-line no-await-in-loop
          await delay(backoffDelay);
          // eslint-disable-next-line no-continue
          continue;
        }

        throw errorInstance;
      }
    }

    throw (
      lastError || new Error('ServiceWorker fetch failed after all retries')
    );
  }

  // Fall back to standard customFetch with retry logic
  return fetchWithoutServiceWorker(uri, options);
};

const singleHttpLink = new HttpLink({
  uri: `${API_DOMAIN}/graphql`,
  headers: customHeaders,
  fetch: customFetchWithServiceWorker,
  ...cors,
});

const httpLink = new BatchHttpLink({
  uri: `${API_DOMAIN}/graphql`,
  batchMax: 5,
  batchInterval: 20,
  headers: customHeaders,
  fetch: customFetchWithServiceWorker,
  ...cors,
});

const errorLink = new ErrorLink(({ error }) => {
  const graphQLErrors = CombinedGraphQLErrors.is(error)
    ? error.errors
    : undefined;
  const networkError = CombinedGraphQLErrors.is(error) ? undefined : error;

  if (hasTechnicalPauseError(graphQLErrors)) {
    handleTechnicalPauseError(graphQLErrors);
  }

  if (
    hasAuthenticationError(graphQLErrors) ||
    (ServerError.is(networkError) && networkError.statusCode === 401)
  ) {
    emitSessionExpired();
    return;
  }

  handleGraphQLErrors(graphQLErrors, captureException);

  const statusCode = ServerError.is(networkError)
    ? networkError.statusCode
    : undefined;
  // AbortError here means the request was cancelled deliberately (component
  // unmount, a newer query superseding this one, page navigation) - not a
  // real failure.
  if (networkError && statusCode !== 429 && !isAbortError(networkError)) {
    captureException(networkError);
  }
});

let hasFetchedToken = false;
const optionsRequestLink = new ApolloLink((operation, forward) => {
  if (hasFetchedToken) {
    // If token has been fetched, proceed with the operation
    return forward(operation);
  }
  return new Observable((observer) => {
    // Send an OPTIONS request
    fetch('/graphql', {
      method: 'OPTIONS',
      credentials: 'include',
    })
      .then(() => {
        hasFetchedToken = true;
      })
      .finally(() => {
        // Proceed with the original request
        forward(operation).subscribe({
          next: observer.next.bind(observer),
          error: observer.error.bind(observer),
          complete: observer.complete.bind(observer),
        });
      })
      .catch((error) => {
        observer.error(error);
      });
  });
});

const sentryTracingLink = new ApolloLink((operation, forward) => {
  return Sentry.startSpan(
    { name: operation.operationName || 'GraphQL Request', op: 'graphql' },
    (span) => {
      return forward(operation).pipe(
        map((response) => {
          span?.end();
          return response;
        }),
      );
    },
  );
});

const customLink = new ApolloLink((operation, forward) => {
  const { operationName } = operation;
  return operationName &&
    ['appFullTask', 'taskCompletionCreate', 'Whoami'].includes(operationName)
    ? singleHttpLink.request(operation, forward)
    : httpLink.request(operation, forward);
});

const loggingLink = createLoggingLink();

const link = ApolloLink.from([
  loggingLink,
  sentryTracingLink,
  optionsRequestLink,
  errorLink,
  customLink,
]);

const Client = new ApolloClient({
  cache: new InMemoryCache(),
  link,
  defaultOptions: {
    query: {
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    },
  } as unknown as ApolloClient.DefaultOptions.Input,
});

export default Client;
